import { Loader, useAPI, Grid, Card } from "components/lib";
import { applyFormTheme, useFormBrandColor } from "components/survey/formTheme";
import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { StylesManager, Model, FunctionFactory } from "survey-core";
import { Survey } from "survey-react-ui";
import Axios from "axios";
import { get, set, del, keys, clear } from "idb-keyval";
import { validatePoaUpload } from "./poa-upload-check";
import { uploadFailureMessage } from "./upload-error";
import { followSafeRedirect } from "./safe-redirect";
import { attachKarbonTfnCheck } from "./karbon-tfn-check";
import { submitForm, OUTCOME_CLOSED } from "./form-submit";
import "survey-core/defaultV2.min.css";
import { v4 as uuidv4 } from "uuid";
// Removed inadvertent express import which pulled in server-only dependencies (send, serve-static, fs, path, stream)
const Settings = require("settings.json");
// Shared ASIC ACN check-digit and ATO TFN checksum validators — same module
// client-onboarding.js uses, so both rules are identical across forms and stay
// unit-tested in one place.
const { validateACN, validateTfn } = require("./australian-identifiers");
const server_url = Settings[process.env.NODE_ENV].server_url;

StylesManager.applyTheme("defaultV2");

/* ─────────────────── Utility functions ────────────────── */
const usePersistentResponseId = () => {
  const [id, setId] = useState(null);
  const params = useParams();
  
  useEffect(() => {
    (async () => {
      const storageKey = `formResponseId-${params.id}`;
      let stored = await get(storageKey);
      if (!stored) {
        stored = uuidv4();
        await set(storageKey, stored);
      }
      setId(stored);
    })();
  }, [params.id]);
  return id;
};

const dataURLtoBlob = (dataURL) => {
  const [meta, content] = dataURL.split(",");
  const mime = meta.match(/:(.*?);/)[1];
  const binary = atob(content);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  return new Blob([array], { type: mime });
};

// Deletes every idb-keyval entry after a submit, the drafts and the stored
// response id with them, as the page always has.
const deleteAllDrafts = () => clear();

/* ───── Upload and Submit Overlay ───── */
// The progress bar shows only when pct is given: the upload passes it, the
// submit does not. At module scope so a progress tick updates it in place.
const Overlay = ({ title, pct }) => (
  <div role="status" aria-live="polite" className="fixed inset-0 grid place-items-center bg-black/50 backdrop-blur-sm z-50">
    <div className="w-80 rounded-2xl bg-white shadow-2xl p-6 flex flex-col items-center gap-4 animate-pop">
      <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
      {typeof pct === "number" && (
        <>
          <progress
            value={pct}
            max={100}
            className="w-full h-3 rounded-full overflow-hidden bg-gray-200 [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-gray-200 [&::-webkit-progress-value]:bg-sg-blue [&::-webkit-progress-value]:rounded-full"
          />
          <span className="text-sm text-gray-600">{pct}%</span>
        </>
      )}
    </div>
  </div>
);

export function PreVerificationFormView(props) {
  // get form details
  const params = useParams();
  const id = params.id;
  const formAPI = useAPI("/api/public/onboarding/" + id);
  const [formData, setFormData] = React.useState(null);
  // `undefined`, not null, is deliberate: useFormBrandColor reads a null theme as
  // "payload resolved, no theme" and would publish it over the frame colour View
  // seeded from the session cache, repainting the default gradient for the whole
  // fetch. The load path below always resolves this to a theme or null.
  const [formTheme, setFormTheme] = useState();

  // frames the page in the form's own colour (R8)
  useFormBrandColor(formTheme);
  const [tfns, setTfns] = React.useState([]);
  // true only on a Karbon onboarding form; until the GET resolves the page
  // adds no Karbon TFN check and the server stays the only one.
  const [karbonOnboarding, setKarbonOnboarding] = React.useState(null);

  /* Persistent ID and caching state */
  const responseId = usePersistentResponseId();
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const isSubmittingRef = useRef(false);
  // Apart from formData and formTheme: the model is memoised on those two.
  const [submitting, setSubmitting] = useState(false);
  const [submitOutcome, setSubmitOutcome] = useState(null);
  const submitMessageRef = useRef(null);

  // get all query params
  const queryParams = new URLSearchParams(window.location.search);

      // Handle file uploads
    const uploadFiles = useCallback(async (sender, options) => {
    console.log("uploadFiles", sender, options);
    const poaCheck = await validatePoaUpload({
      files: options.files,
      fieldName: options.name,
      formSchema: formData,
      onboardingId: id,
      serverUrl: server_url,
    });

    if (!poaCheck.valid) {
      options.callback("error", poaCheck.message);
      return;
    }

    const uploadData = new FormData();
    options.files.forEach(function (file) {
      uploadData.append("file", file);
      uploadData.append("response_id", responseId);
      uploadData.append("field_name", options.name);
      uploadData.append("file_name", file.name);
    });
    Axios.post(server_url + "/api/utility/gcp/upload", uploadData).then((data) => {
      console.log("data", data);
      options.callback(
        "success",
        options.files.map(function (file) {
          return {
            file: file,
            name: data.data.name,
            type: data.data.name,
            content: data.data.url,
            id: file.id,
          };
        })
      );
    }).catch((err) => {
      options.callback("error", uploadFailureMessage(err));
    });
  });
  

  /* ───────────── Submit ───────────── */
  // The live onComplete handler. The listener effect calls it through
  // onCompleteRef, so the listener stays the same while formData and the
  // other values read here change. form-submit.js holds the steps.
  const handleComplete = (sender, options) =>
    submitForm(sender, options, {
      schema: formData,
      responseId,
      guard: isSubmittingRef,
      post: (answers) => Axios.post("/api/public/onboarding/" + params.id, answers),
      clearDrafts: deleteAllDrafts,
      onSubmitting: (on) => {
        // A new submit drops the message from the last failed one.
        if (on) setSubmitOutcome(null);
        setSubmitting(on);
      },
      onOutcome: setSubmitOutcome,
    });
  const onCompleteRef = useRef(handleComplete);
  onCompleteRef.current = handleComplete;

  React.useEffect(() => {
    if (!formAPI?.loading && formAPI?.data !== null) {
      console.log("formAPI", formAPI?.data);
      const schema = formAPI?.data?.schema || null;
      setFormData(schema);
      setFormTheme(formAPI?.data?.theme || null);
      setTfns(schema?.tfns);
      setKarbonOnboarding(formAPI?.data?.karbon_onboarding);
    }
  }, [formAPI]);

  /* ───────────── File upload handling ───────────── */
  const remoteUpload = async (blob, field, fname) => {
    const fd = new FormData();
    fd.append("file", blob, fname);
    fd.append("response_id", responseId);
    fd.append("key", `${responseId}/${field}/${fname}`);
    fd.append("field_name", field);
    fd.append("file_name", fname);
    const res = await Axios.post(`${server_url}/api/utility/gcp/upload`, fd);
    if (res.status !== 200) throw new Error("File upload failed");
    return { name: fname, type: blob.type || "application/octet-stream", url: res.data.url, key: `${responseId}/${field}/${fname}`};
  };

  const handleUploadsAndSubmit = async (data, url) => {
    const formModel = new Model(formData);
    const fileQs = formModel.getAllQuestions().filter((q) => q.getType() === "file" && data[q.name]?.length);
    const total = fileQs.reduce((s, q) => s + (data[q.name]?.length || 0), 0);
    let done = 0;

    if (total > 0) {
      setBusy(true);
      setPct(0);
    }

    const processedData = { ...data };

    for (const q of fileQs) {
      const replacements = [];
      for (const f of data[q.name] || []) {
        const blob = (await get(`${responseId}/${q.name}/${f.name}`)) || dataURLtoBlob(f.content);
        const uploadResult = await remoteUpload(blob, q.name, f.name);
        // Retain the key, file name, type, and URL
        replacements.push({
          key: uploadResult.key,
          name: uploadResult.name,
          type: uploadResult.type,
          url: uploadResult.url
        });
        done += 1;
        setPct(Math.round((done / total) * 100));
      }
      processedData[q.name] = replacements;
    }

    const response = await Axios.post(url, { ...processedData , id: responseId });
    console.log("response", response);
    
    if (total > 0) {
      setBusy(false);
    }

    // Clear cached data on successful submission
    if (responseId) {
      const allKeys = await keys();
      const formKeys = allKeys.filter(k => k.includes(`formDraft-${id}`) || k.includes(responseId));
      for (const k of formKeys) {
        await del(k);
      }
    }

    // Only an absolute http(s) redirect is followed (stored XSS guard).
    followSafeRedirect(response.data.redirect);
  };

  function isValidUtr(sender, options) {
    console.log("isValidUTR", sender, options);
    const utrQuestion = sender.getQuestionByName("utr");
    console.log("utrQuestion", utrQuestion);

    if (!utrQuestion) {
      return;
    }

    if (utrQuestion.isRequired && !options.value) {
      console.log("UTR is required");
      options.error = "UTR is required";
    }

    if (!utrQuestion.isRequired && !options.value) {
      return;
    }

    if (options.name === "utr") {
      let utr = (options.value || "").toString();
      console.log("utr", utr);

      // Check if UTR length is 10 digits
      if (utr.length !== 10) {
        return (options.error = "UTR must be 10 digits");
      }

      // Define the weighting factors
      const weights = [0, 6, 7, 8, 9, 10, 5, 4, 3, 2];
      const remainderWeights = [2, 1, 9, 8, 7, 6, 5, 4, 3, 2, 1];

      let sum = 0;

      weights.forEach((weight, position) => {
        const digit = Number(utr[position]);
        sum += weight * digit;
      });
      console.log("sum", sum);

      const checkDigit = sum % 11;
      const expectedCheckDigit = remainderWeights[checkDigit];

      console.log("checkDigit", checkDigit);
      console.log("expectedCheckDigit", expectedCheckDigit);

      if (expectedCheckDigit !== Number(utr.charAt(0))) {
        return (options.error = "Invalid UTR");
      }
    }
  }

  function isValidABN(sender, options) {
    // console.log("isValidABN", sender, options);
    const abnQuestion = sender.getQuestionByName("abn");
    // console.log("abnQuestion", abnQuestion);

    if (!abnQuestion) {
      return;
    }

    if (abnQuestion.isRequired && !options.value) {
      console.log("ABN is required");
      options.error = "ABN is required";
    }

    if (!abnQuestion.isRequired && !options.value) {
      return;
    }

    if (options.name === "abn") {
      console.log("ABN question found", options.value);
      let abn = (options.value || "").toString();

      // Check if ABN length is 11 digits
      if (abn.length !== 11) {
        return (options.error = "ABN must be 11 digits");
      }

      // Subtract 1 from the first digit
      abn = (parseInt(abn.charAt(0)) - 1).toString() + abn.substring(1);

      // Define the weighting factors
      const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

      // Calculate the sum of the products
      let sum = 0;
      for (let i = 0; i < abn.length; i++) {
        sum += parseInt(abn.charAt(i)) * weights[i];
      }

      // Check if sum modulo 89 is 0
      const isvalid = sum % 89 === 0;
      if (!isvalid) {
        return (options.error = "Invalid ABN");
      }
    }
  }

  // Block an ACN that fails the ASIC check digit at entry: XPM rejects a
  // malformed ACN, so catching it here is the difference between a fixable
  // field error and a silently dropped Company Number. Matched by name
  // substring (panel rows may be `acn`, `companyAcn`, `trustee_acn`, …), the
  // same rule client-onboarding.js uses. Empty is left to the question's own
  // isRequired.
  function isValidACN(sender, options) {
    const question = options.question;
    if (question.name.toLowerCase().includes("acn") && options.value) {
      const error = validateACN(options.value);
      if (error) return (options.error = error);
    }
  }

  const model = useMemo(
    () => (formData ? applyFormTheme(new Model(formData), formTheme) : null),
    [formData, formTheme]
  );
  
  /* ───────────── Setup model effects ───────────── */
  useEffect(() => {
    if (!model || !responseId) return;

    // Restore draft data
    const restoreDraft = async () => {
      const draftKey = `formDraft-${id}`;
      const draft = await get(draftKey);
      if (draft) {
        model.data = draft;
      }
    };
    restoreDraft();

    // Auto-save draft on value changes
    const saveDraft = () => {
      const draftKey = `formDraft-${id}`;
      set(draftKey, model.data);
    };
    model.onValueChanged.add(saveDraft);

    // A stable wrapper: each call reads the live handler from the ref.
    const onComplete = (sender, options) => onCompleteRef.current(sender, options);
    model.onUploadFiles.add(uploadFiles);
    model.onComplete.add(onComplete);

    return () => {
      model.onValueChanged.remove(saveDraft);
      model.onUploadFiles.remove(uploadFiles);
      model.onComplete.remove(onComplete);
    };
    // uploadFiles is rebuilt each render, but it reads only formData (fixed
    // per model), id and responseId, so the one added here is never stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, responseId, id]);

  /* ───────────── Setup query params and validation ───────────── */
  useEffect(() => {
    if (!model) return;

    model.onAfterRenderSurvey.add(() => {
      console.log("onAfterRenderSurvey", model);
      queryParams.forEach((value, key) => {
        console.log("query param", key, value);
        const question = model.getQuestionByName(key);
        if (question) {
          console.log("setting value for question", key, value);
          question.value = value;
        } else {
          console.log("question not found", key);
        }
      });
    });

    // Add validation handlers
    model.onValidateQuestion.add(isValidABN);
    model.onValidateQuestion.add(isValidACN);
    model.onValidateQuestion.add((sender, options) => {
    // console.log("onValidateQuestion", sender, options);
    const question = options.question;
    console.log("question", question);
    // parse as json
    const tfnsJson = Array.isArray(tfns) ? tfns : JSON.parse(tfns || "[]");
    // console.log("tfnsJson", tfnsJson);

    const validateABN = (abnValue) => {
      let abn = (options.value || "").toString();

      // Check if ABN length is 11 digits
      if (abnValue.length !== 11) {
        return (options.error = "ABN must be 11 digits");
      }

      // Subtract 1 from the first digit
      abnValue = (parseInt(abnValue.charAt(0)) - 1).toString() + abnValue.substring(1);

      // Define the weighting factors
      const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

      // Calculate the sum of the products
      let sum = 0;
      for (let i = 0; i < abnValue.length; i++) {
        sum += parseInt(abnValue.charAt(i)) * weights[i];
      }

      // Check if sum modulo 89 is 0
      const isvalid = sum % 89 === 0;
      if (!isvalid) {
        return "Invalid ABN";
      } else {
        return null;
      }
    }

    tfnsJson.forEach((tfn) => {
      // console.log("tfn", tfn);
      if (tfn.name === question.name) {
        console.log("tfn question found", question.name);

        if (!options.value && question.isRequired) {
          return (options.error = "TFN is required");
        }
  
        if (!options.value && !question.isRequired) {
          return;
        }

        // setTfnsValues([...tfnValues, { name: question.name, value: options.value }]);
        // tfnValues.current = [...tfnValues.current, { name: question.name, value: options.value }];
        const response = validateTfn(options.value);
        return (options.error = response);
      }
    });

    if (question.name === "abn") {
      console.log("abn question found", question.name, options.value);
      if (!options.value && question.isRequired) {
        return (options.error = "ABN is required");
      }

      if (!options.value && !question.isRequired) {
        return;
      }
      const response = validateABN(options.value);
      return (options.error = response);
    }
    });
  }, [model, tfns]);

  /* ───────────── Karbon TFN check ───────────── */
  // Its own effect, declared after the validation effect so its handler runs
  // after the ones above. It only ever sets an error, never clears one.
  useEffect(() => {
    if (!model) return;
    return attachKarbonTfnCheck(model, {
      enabled: karbonOnboarding,
      schema: formData,
    });
  }, [model, karbonOnboarding, formData]);

  // A failed submit shows its message above the form; bring it into view.
  useEffect(() => {
    if (submitOutcome?.message && submitMessageRef.current?.scrollIntoView) {
      submitMessageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [submitOutcome]);

  /* ───────────── Guard render while loading ───────────── */
  if (formAPI.loading || !responseId) {
    return <Loader />;
  }

  if (!model) {
    return <Loader />;
  }

  // After a 5xx the form stays closed: only the message shows.
  const formClosed = submitOutcome?.kind === OUTCOME_CLOSED;
  const fieldLines = submitOutcome?.fieldLines || [];

  return (
    <>
      <div className="min-w-min w-3/4">
        <Grid>
          <Card>
            {submitOutcome?.message && (
              <div
                ref={submitMessageRef}
                role="alert"
                className="mb-4 p-4 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm"
              >
                <p className="font-semibold">{submitOutcome.message}</p>
                {fieldLines.length > 0 && (
                  <ul className="pl-5 mt-2 space-y-1" style={{ listStyleType: "disc" }}>
                    {fieldLines.map((line, index) => (
                      <li key={index}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {!formClosed && <Survey model={model} />}
          </Card>
        </Grid>
      </div>
      {submitting && <Overlay title="Submitting your form…" />}
      {busy && <Overlay title="Uploading your files…" pct={pct} />}
    </>
  );
}

export default PreVerificationFormView;
